import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import { ONBOARDING_AGENT_QUICK_ACTION_TYPE } from "../onboardingAgent.types";
import { ONBOARDING_AGENT_LOG_PREFIX } from "../onboardingAgentConstants";
import { onboardingAgentTrackingService } from "../onboardingAgentTrackingService";
import { ONBOARDING_AGENT_MESSAGE_QUICK_ACTIONS } from "../texts";

import { goToSuggestedPage } from "./helpers/suggestedPageNavigation";
import type { OnboardingAgentAssistantQuickAction } from "./OnboardingAgentAssistantMessage.types";
import { QuickActionButton, QuickActionsLabel, QuickActionsRow, QuickActionsSection } from "./styles";

const ONBOARDING_AGENT_ACTION_TARGET = {
    sales: "route:sales",
    demo: "route:demo",
    support: "route:support",
    subscription: "route:subscription",
    subscriptionPricing: "route:subscription-pricing",
    pricing: "route:pricing",
} as const;

const CONTACT_SALES_LINK = "https://www.similarweb.com/corp/contact-us/";
const BOOK_DEMO_LINK = "https://similarweb.chilipiper.com/round-robin/csm-team";
const SUPPORT_HOMEPAGE_LINK = "https://support.similarweb.com/hc/en-us";
const SUBSCRIPTION_PRICING_LINK = "https://account.similarweb.com/subscription-information";

const ACCOUNT_TARGET_PREFIX = "account:";
const ACCOUNT_BASE_URL = "https://account.similarweb.com";

const resolveAccountTarget = (target: string): string | null => {
    if (!target.startsWith(ACCOUNT_TARGET_PREFIX)) {
        return null;
    }
    const path = target.slice(ACCOUNT_TARGET_PREFIX.length);
    return `${ACCOUNT_BASE_URL}/${path}`;
};

const EXTERNAL_LINK_TARGET = "_blank";
const EXTERNAL_LINK_FEATURES = "noopener,noreferrer";

const openExternalLink = (url: string): void => {
    window.open(url, EXTERNAL_LINK_TARGET, EXTERNAL_LINK_FEATURES);
};

export type OnboardingAgentAssistantQuickActionsProps = {
    readonly messageIndex: number;
    readonly actions: readonly OnboardingAgentAssistantQuickAction[];
    readonly threadId?: string;
};

const dispatchQuickAction = (action: OnboardingAgentAssistantQuickAction): void => {
    if (action.type === ONBOARDING_AGENT_QUICK_ACTION_TYPE.navigate) {
        void goToSuggestedPage({ uiRouterState: action.target });
        return;
    }

    if (action.type === ONBOARDING_AGENT_QUICK_ACTION_TYPE.action) {
        if (action.target === ONBOARDING_AGENT_ACTION_TARGET.sales) {
            openExternalLink(CONTACT_SALES_LINK);
            return;
        }

        if (action.target === ONBOARDING_AGENT_ACTION_TARGET.demo) {
            openExternalLink(BOOK_DEMO_LINK);
            return;
        }

        if (action.target === ONBOARDING_AGENT_ACTION_TARGET.support) {
            openExternalLink(SUPPORT_HOMEPAGE_LINK);
            return;
        }

        if (
            action.target === ONBOARDING_AGENT_ACTION_TARGET.subscription ||
            action.target === ONBOARDING_AGENT_ACTION_TARGET.subscriptionPricing ||
            action.target === ONBOARDING_AGENT_ACTION_TARGET.pricing
        ) {
            openExternalLink(SUBSCRIPTION_PRICING_LINK);
            return;
        }

        const accountUrl = resolveAccountTarget(action.target);
        if (accountUrl !== null) {
            openExternalLink(accountUrl);
            return;
        }
    }

    Sentry.warning(`${ONBOARDING_AGENT_LOG_PREFIX} Unknown quick action type: ${action.type}`);
};

export const OnboardingAgentAssistantQuickActions = ({
    messageIndex,
    actions,
    threadId,
}: OnboardingAgentAssistantQuickActionsProps) => {
    const { translate } = useTranslate();
    const visibleActions = actions.filter((action) => action.type !== ONBOARDING_AGENT_QUICK_ACTION_TYPE.navigate);

    if (visibleActions.length === 0) {
        return null;
    }

    return (
        <QuickActionsSection data-automation={`onboarding-agent-message-${messageIndex}-quick-actions`}>
            <QuickActionsLabel data-automation={`onboarding-agent-message-${messageIndex}-quick-actions-label`}>
                {translate(ONBOARDING_AGENT_MESSAGE_QUICK_ACTIONS)}
            </QuickActionsLabel>
            <QuickActionsRow>
                {visibleActions.map((action, actionIndex) => {
                    const automation = `onboarding-agent-message-${messageIndex}-quick-action-${actionIndex}`;
                    const handleClick = (): void => {
                        onboardingAgentTrackingService.trackQuickLinksClick({
                            clickedQuickLinks: `${action.label}: ${action.type}/${action.target}`,
                            threadId,
                        });
                        dispatchQuickAction(action);
                    };

                    return (
                        <QuickActionButton
                            key={`${action.label}-${action.type}-${action.target}-${actionIndex}`}
                            type="button"
                            data-automation={automation}
                            onClick={handleClick}
                        >
                            {action.label}
                        </QuickActionButton>
                    );
                })}
            </QuickActionsRow>
        </QuickActionsSection>
    );
};
